import defu from 'defu'
import {defineStore} from 'pinia'
import {
  Invoices,
  Notification,
  Notifications,
  NotificationType,
  PaginationQueryParams,
  Profile,
  User,
} from '~/types/types'
import {useApi} from "~/composables/useApi";

export const useUserStore = defineStore('user', () => {
    const user = ref<User | null>(null)
    const subjects = ref<any>(null)
    const activeSubject = ref<any>(null)
    const profile = ref<Profile>()
    const pwaInstallEvent = ref<Event | null>(null)
    const topUnreadNotifications = ref<
      Notification<'WasteCollection' | 'Invoice' | 'WasteCollectionYardReport' | 'WasteLimitReached'>[]
    >([])
    const notifications = ref<Notifications>(null)
    const invoices = ref<Invoices>(null)
    const payments = ref<any>(null)

    const fetchUser = async () => {
      const response = await useApi('/user/user-info')

      if (!response.error.value) {
        const data: any = response.data.value

        const fallbackLang =
          navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language

        user.value = {
          ...data,
          id: data.id,
          name: data.fullName,
          initials: getInitials(data.fullName),
          userName: data.userName,
          lang: data?.municipality?.countryCulture ? data.municipality.countryCulture : fallbackLang,
          /*
          subject: {
            id: data.subject.id === -1 ? 1 : data.subject.id,
            city: data.subject.city,
            street: data.subject.street,
            zipCode: data.subject.zipCode,
          },
           */
        }
        /*
        if (!activeYard.value) {
          activeYard.value = data?.subject
        }*/
      }

      return response
    }
    const fetchSubjects = async () => {
      const response = await useApi('/user/user-info/managed-subjects')

      let returnData = null
      if (!response.error.value) {
        const data: any = response.data.value
        returnData = data
      }
      subjects.value = returnData?.items
      if (returnData?.items?.length > 0 && !activeSubject.value) {
        activeSubject.value = returnData?.items[0]
      }
      return returnData
    }

    const fetchProfile = async () => {
      const response = await useApi('/user/profile')

      if (!response.error.value) {
        const data: any = response.data.value

        profile.value = {
          id: data.id,
          name: data.fullName,
          userName: data.userName,
          initials: getInitials(data.fullName ? data.fullName : data.userName),
          email: data.email,
          phone: data.phone,
          street: data.streetAddress,
          city: data.city,
          zip: data.zipCode,
        }
      }

      return response
    }

    const updateProfile = async (data: {
      email: string
      phone: string
      oldPassword: string
      password: string | null
    }) => {
      return useApi('/user/profile', {
        method: 'PUT',
        body: data.password
          ? {email: data.email, phone: data.phone, oldPassword: data.oldPassword, password: data.password}
          : {email: data.email, phone: data.phone, oldPassword: data.oldPassword},
      })
    }

    const deactivateAccount = async () => {
      return await useApi('/user/profile', {method: 'DELETE'})
    }

    const fetchTopUnreadNotifications = async () => {
      const {$moment} = useNuxtApp()

      const response = await useApi('/user/notification', {
        params: {
          UnreadOnly: true,
          Limit: 9,
        },
      })

      if (!response.error.value) {
        const data: any = response.data.value

        topUnreadNotifications.value = data.items
          .filter((notification: any) => notification.type !== 'Undefined')
          .map((notification: any) => ({
            id: notification.id,
            type: notification.type,
            isRead: notification.isRead,
            createdTs: $moment(notification.createdTs),
            data: transformNotificationData(notification.type, JSON.parse(notification.data)),
          }))
      }
    }

    const fetchNotifications = async (
      paginationParams: PaginationQueryParams | null = null,
      unreadOnly: boolean | null = null
    ) => {
      const {$moment} = useNuxtApp()

      const response = await useApi('/user/notification', {
        query: defu(
          unreadOnly ? {UnreadOnly: unreadOnly} : {},
          paginationParams?.page ? {Page: paginationParams.page} : {}
        ),
      })

      if (!response.error.value) {
        const data: any = response.data.value

        const items = data.items
          .filter((notification: any) => notification.type !== 'Undefined')
          .map((notification: any) => ({
            id: notification.id,
            type: notification.type,
            isRead: notification.isRead,
            createdTs: $moment(notification.createdTs),
            data: transformNotificationData(notification.type, JSON.parse(notification.data)),
          }))

        notifications.value = {
          items,
          pagination: {
            page: data.page,
            pageCount: data.pageCount,
            limit: data.limit,
            total: data.total,
          },
        }
        return {
          items,
          pagination: {
            page: data.page,
            pageCount: data.pageCount,
            limit: data.limit,
            total: data.total,
          },
        }
      }

      return response
    }

    const markNotificationAsRead = async (id: number) => {
      const response = await useApi(`/user/notification/${id}`, {
        method: 'PATCH',
      })

      if (!response.error.value) {
        const notificationsIndex = notifications.value?.items?.findIndex(n => n.id === id) ?? -1

        if (notificationsIndex !== -1) {
          notifications.value.items[notificationsIndex].isRead = true
        }
      }

      return response
    }

    const markAllTopNotificationsAsRead = async () => {
      const unread = topUnreadNotifications.value.filter(n => !n.isRead)
      if (unread.length === 0) return

      await Promise.all(
        unread.map(n => useApi(`/user/notification/${n.id}`, { method: 'PATCH' }))
      )

      await fetchTopUnreadNotifications()
    }

    const fetchPayments = async (params) => {
      if (!params['SubjectIds[]'] || !params['SubjectIds[]'].length) {
        return
      }

      const {data: response, error} = await useApi(`/subject/payments`, {query: params});

      if (error.value) {
        throw new Error('Error fetching payments');
      }

      payments.value = response.value;

      return response.value
    }

    const fetchInvoices = async (
      paginationParams: PaginationQueryParams = null,
      type: string | null,
      feePaid: boolean | null = null,
      search: string | null = null,
      processedFrom: string | null = null,
      processedTo: string | null = null
    ) => {
      const {$moment} = useNuxtApp()

      const response = await useApi('/user/profile/invoice-history', {
        query: defu(
          paginationParams?.page ? {Page: paginationParams.page} : {},
          paginationParams?.limit ? {Limit: paginationParams.limit} : {},
          paginationParams?.orderBy?.column ? {OrderByCol: paginationParams.orderBy.column} : {},
          paginationParams?.orderBy?.direction ? {OrderbyDirection: paginationParams.orderBy.direction} : {},
          type ? {Type: type} : {},
          search ? {SearchText: search} : {},
          feePaid !== null ? {FeePaid: feePaid} : {},
          processedFrom ? {'startReportTs.From': processedFrom} : {},
          processedTo ? {'ProcessedTs.To': processedTo} : {}
        ),
      })

      if (!response.error.value) {
        const data: any = response.data.value

        const items = data.items.map((item: any) => ({
          id: item.id,
          invoice: item.invoice,
          payment: item.payment,
          totalFee: item.totalFee,
          paymentType: item.paymentType,
          startReportTs: $moment(item?.startReportTs),
          endReportTs: $moment(item?.endReportTs),
          subjectId: item.subjectId,
          subjectName: item.subjectName,
          feePaid: item.feePaid,
          type: item.type,
          wasteCollectionYardId: item.wasteCollectionYardId,
          wasteCollectionYardName: item.wasteCollectionYardName,
        }))

        invoices.value = {
          items,
          pagination: {
            page: data.page,
            pageCount: data.pageCount,
            limit: data.limit,
            total: data.total,
          },
        }

        return data
      }

      return response
    }

    const exportInvoices = async (params: {
      feePaid?: boolean | null
      search?: string | null
      processedFrom?: string | null
      processedTo?: string | null
    }) => {
      return useApi('/user/profile/invoice-history/export', {
        query: defu(
          params.search ? {SearchText: params.search} : {},
          [null, undefined].includes(params.feePaid) ? {} : {FeePaid: params.feePaid},
          params.processedFrom ? {'ProcessedTs.From': params.processedFrom} : {},
          params.processedTo ? {'ProcessedTs.To': params.processedTo} : {}
        ),
        responseType: 'blob',
      })
    }

    const getInitials = (name: string): string => {
      const words = name.split(' ').filter(word => word)

      return words
        .map(word => word[0].toUpperCase())
        .join('')
        .slice(0, 2)
    }

    const setFavouriteYard = async (id: number) => {
      return useApi('/user/profile/waste-collection-yard/favourite', {
        method: 'PATCH',
        query: {wasteCollectionYardId: id},
      });
    }

    const transformNotificationData = (type: NotificationType, data: any) => {
      const {$moment} = useNuxtApp()

      if (type === 'Invoice') {
        return {
          id: data.Id,
          invoice: data.Invoice,
          recordedTs: $moment(data.RecordedTs),
        }
      }

      if (type === 'WasteCollection' || type === 'WasteCollectionYardReport') {
        return {
          id: data.Id,
          wasteName: data.WasteName,
          wasteColor: isEmpty(data.WasteColor) ? '#000000' : data.WasteColor,
          weight: data.Weight,
          recordedTs: $moment(data.RecordedTs),
        }
      }

      if (type === 'WasteLimitReached') {
        return {
          id: data.Id,
          wasteName: data.WasteName,
          wasteColor: isEmpty(data.WasteColor) ? '#000000' : data.WasteColor,
          recordedTs: $moment(data.RecordedTs),
        }
      }
    }

    return {
      user,
      subjects,
      profile,
      topUnreadNotifications,
      notifications,
      invoices,
      payments,
      fetchPayments,
      activeSubject,
      fetchUser,
      fetchProfile,
      updateProfile,
      deactivateAccount,
      fetchTopUnreadNotifications,
      fetchNotifications,
      markNotificationAsRead,
      markAllTopNotificationsAsRead,
      fetchInvoices,
      fetchSubjects,
      exportInvoices,
      pwaInstallEvent,
      setFavouriteYard,
    }
  }
)
